import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTagColors, getAdminTagColors, updateTagColors, deleteTagColor, getInventoryTags, getBuyerTags, setBuyerTag,
} from '../api';
import { reportKeys } from './useReports';

export const tagColorKeys = {
  all: ['tagColors'],
  admin: ['tagColors', 'admin'],
  inventoryTags: ['inventoryTags'],
  buyerTags: ['buyerTags'],
};

export function useTagColors(options = {}) {
  return useQuery({
    queryKey: tagColorKeys.all,
    queryFn: async () => {
      const res = await getTagColors();
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useAdminTagColors(options = {}) {
  return useQuery({
    queryKey: tagColorKeys.admin,
    queryFn: async () => {
      const res = await getAdminTagColors();
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useInventoryTags(options = {}) {
  return useQuery({
    queryKey: tagColorKeys.inventoryTags,
    queryFn: async () => {
      const res = await getInventoryTags();
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useUpdateTagColors() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTagColors,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagColorKeys.all });
      queryClient.invalidateQueries({ queryKey: tagColorKeys.admin });
    },
  });
}

export function useDeleteTagColor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTagColor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagColorKeys.all });
      queryClient.invalidateQueries({ queryKey: tagColorKeys.admin });
    },
  });
}

// The tags ticked as Buyer on Admin → Tags (string[]): Reports → Purchases by tag reads only these
export function useBuyerTags(options = {}) {
  return useQuery({
    queryKey: tagColorKeys.buyerTags,
    queryFn: async () => {
      const res = await getBuyerTags();
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

// Tick or untick one tag as a buyer: mutate({ tag, isBuyer }). It stays
// pending until the buyer tags are read back, so the box never flips back
// for a moment. The reports are refetched too, though the server still
// answers a report URL it worked out in the last 60 s from memory.
export function useSetBuyerTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tag, isBuyer }) => setBuyerTag(tag, isBuyer),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.all });
      return queryClient.invalidateQueries({ queryKey: tagColorKeys.buyerTags });
    },
  });
}
